/**
 * "Loose" TA primitives shared by the CUE indicators — value at EVERY bar
 * (using min(window, available) during warmup, never `na`/null), matching the
 * Pine ports in `lib/indicators/customIndexEtf.ts` / `customMda.ts` exactly.
 * These are the single source for the CUE math so the chart studies and the
 * shared `SharedIndicatorDef`s compute identical numbers (parity-verified).
 *
 * Distinct from `lib/indicators/shared/ema.ts` (strict index-aligned EMA used by
 * the continuous technicals) — the CUE Pine source is "loose", so it gets its
 * own faithful copies here.
 */

/** Simple MA over min(length, available) — value at every index. */
export function smaFull(values: ReadonlyArray<number>, length: number): number[] {
  const out: number[] = new Array(values.length);
  let sum = 0;
  for (let i = 0; i < values.length; i++) {
    sum += values[i]!;
    if (i >= length) sum -= values[i - length]!;
    out[i] = sum / Math.min(i + 1, length);
  }
  return out;
}

/** Linearly-weighted MA over min(length, available) — Pine ta.wma. */
export function wmaFull(values: ReadonlyArray<number>, length: number): number[] {
  const out: number[] = new Array(values.length);
  for (let i = 0; i < values.length; i++) {
    const n = Math.min(i + 1, length);
    let wsum = 0;
    let wd = 0;
    for (let k = 0; k < n; k++) {
      const w = n - k; // k=0 is the current bar → weight n
      wsum += values[i - k]! * w;
      wd += w;
    }
    out[i] = wsum / wd;
  }
  return out;
}

/** EMA seeded with the first value (loose — value at every index). */
export function emaFull(values: ReadonlyArray<number>, length: number): number[] {
  const out: number[] = new Array(values.length);
  const a = 2 / (length + 1);
  let prev = values.length > 0 ? values[0]! : 0;
  for (let i = 0; i < values.length; i++) {
    prev = i === 0 ? values[0]! : a * values[i]! + (1 - a) * prev;
    out[i] = prev;
  }
  return out;
}

/** Wilder's RMA — EMA with α = 1/length, seeded at the first value (loose). */
export function rmaFull(values: ReadonlyArray<number>, length: number): number[] {
  const out: number[] = new Array(values.length);
  const a = 1 / length;
  let prev = values.length > 0 ? values[0]! : 0;
  for (let i = 0; i < values.length; i++) {
    prev = i === 0 ? values[0]! : a * values[i]! + (1 - a) * prev;
    out[i] = prev;
  }
  return out;
}

/** Selectable moving average over a (null-free) series: 0 EMA · 1 SMA · 2 RMA ·
 *  3 WMA. Used by the configurable OBV/RS "MA Age" indicators. */
export const MA_TYPE_CHOICES = [
  { value: 0, label: "EMA" },
  { value: 1, label: "SMA" },
  { value: 2, label: "RMA" },
  { value: 3, label: "WMA" },
] as const;

export function maFull(
  values: ReadonlyArray<number>,
  length: number,
  maType: number,
): number[] {
  switch (maType) {
    case 1:
      return smaFull(values, length);
    case 2:
      return rmaFull(values, length);
    case 3:
      return wmaFull(values, length);
    default:
      return emaFull(values, length);
  }
}

/** Signed bars-since `series` last crossed its `line`: +N = N bars at/above the
 *  line, −N = N bars below, 0 before the first cross. Both arrays index-aligned;
 *  same convention as the RSMA/OBV cross counts. */
export function signedMaCrossAge(
  series: ReadonlyArray<number>,
  line: ReadonlyArray<number>,
): number[] {
  const n = series.length;
  const out = new Array<number>(n).fill(0);
  let buy = 0;
  let sell = 0;
  for (let i = 1; i < n; i++) {
    if (series[i]! >= line[i]! && series[i - 1]! < line[i - 1]!) {
      buy = 1;
      sell = 0;
    } else if (series[i]! < line[i]! && series[i - 1]! >= line[i - 1]!) {
      sell = 1;
      buy = 0;
    } else {
      if (buy !== 0) buy += 1;
      if (sell !== 0) sell += 1;
    }
    out[i] = buy > 0 ? buy : sell > 0 ? -sell : 0;
  }
  return out;
}

/** Triple EMA — 3*e1 - 3*e2 + e3 (Pine ta.tema). */
export function temaFull(values: ReadonlyArray<number>, length: number): number[] {
  const e1 = emaFull(values, length);
  const e2 = emaFull(e1, length);
  const e3 = emaFull(e2, length);
  return Array.from(values, (_, i) => 3 * e1[i]! - 3 * e2[i]! + e3[i]!);
}

/** Population standard deviation over min(length, available) — Pine
 *  ta.stdev(src, len, biased = true). */
export function stdevFull(values: ReadonlyArray<number>, length: number): number[] {
  const out: number[] = new Array(values.length);
  for (let i = 0; i < values.length; i++) {
    const n = Math.min(i + 1, length);
    let mean = 0;
    for (let k = i - n + 1; k <= i; k++) mean += values[k]!;
    mean /= n;
    let v = 0;
    for (let k = i - n + 1; k <= i; k++) v += (values[k]! - mean) ** 2;
    out[i] = Math.sqrt(v / n);
  }
  return out;
}

/** True range per bar, from index-aligned highs/lows/closes. */
export function trueRangeFull(
  highs: ReadonlyArray<number>,
  lows: ReadonlyArray<number>,
  closes: ReadonlyArray<number>,
): number[] {
  const n = closes.length;
  const out: number[] = new Array(n);
  for (let i = 0; i < n; i++) {
    const h = highs[i]!;
    const l = lows[i]!;
    if (i === 0) {
      out[i] = h - l;
      continue;
    }
    const pc = closes[i - 1]!;
    out[i] = Math.max(h - l, Math.abs(h - pc), Math.abs(l - pc));
  }
  return out;
}

/** ATR — Wilder RMA of true range, seeded loosely at bar 0. */
export function atrFull(
  highs: ReadonlyArray<number>,
  lows: ReadonlyArray<number>,
  closes: ReadonlyArray<number>,
  length: number,
): number[] {
  const tr = trueRangeFull(highs, lows, closes);
  const out: number[] = new Array(tr.length);
  const a = 1 / length;
  let prev = tr.length > 0 ? tr[0]! : 0;
  for (let i = 0; i < tr.length; i++) {
    prev = i === 0 ? tr[0]! : a * tr[i]! + (1 - a) * prev;
    out[i] = prev;
  }
  return out;
}

/** Rolling max of `values` over [i-length+1, i] (clamped to bar 0). */
export function highestAt(values: ReadonlyArray<number>, length: number, i: number): number {
  let m = -Infinity;
  for (let k = Math.max(0, i - length + 1); k <= i; k++) if (values[k]! > m) m = values[k]!;
  return m;
}

/** Rolling min of `values` over [i-length+1, i] (clamped to bar 0). */
export function lowestAt(values: ReadonlyArray<number>, length: number, i: number): number {
  let m = Infinity;
  for (let k = Math.max(0, i - length + 1); k <= i; k++) if (values[k]! < m) m = values[k]!;
  return m;
}

/**
 * Parabolic SAR — TradingView `ta.sar(start, inc, max)`.
 *
 * This is the official `pine_sar` from the Pine v5/v6 `ta.sar` reference
 * (the snippet TV documents as identical to the builtin), not the older
 * Wilder-style seed this file used to carry. AFIAT Dots plots
 * `close > ta.sar(0.02, 0.02, 0.2)`. The previous port seeded bar 0,
 * used `>=` for the first trend, set a flip to the old EP alone, and
 * advanced AF on the first bar of a trend — that is why the Dots PSAR
 * lane disagreed with TradingView on MU.
 *
 * Bar 0 is `NaN`. Direction on bar 1 is `close > close[1]` (strict).
 * A flip sets SAR to `max(high, old EP)` / `min(low, old EP)`, not EP
 * alone, and AF does not increment on the first bar of a trend.
 */
export function psarFull(
  highs: ReadonlyArray<number>,
  lows: ReadonlyArray<number>,
  closes: ReadonlyArray<number>,
  start = 0.02,
  inc = 0.02,
  max = 0.2,
): number[] {
  const n = closes.length;
  const out: number[] = new Array(n).fill(NaN);
  if (n === 0) return out;

  let result = NaN;
  let maxMin = NaN;
  let acceleration = NaN;
  let isBelow = false;

  for (let i = 0; i < n; i++) {
    let isFirstTrendBar = false;
    if (i === 1) {
      if (closes[i]! > closes[i - 1]!) {
        isBelow = true;
        maxMin = highs[i]!;
        result = lows[i - 1]!;
      } else {
        isBelow = false;
        maxMin = lows[i]!;
        result = highs[i - 1]!;
      }
      isFirstTrendBar = true;
      acceleration = start;
    }
    if (i < 1 || !Number.isFinite(result)) continue;

    result = result + acceleration * (maxMin - result);

    if (isBelow) {
      if (result > lows[i]!) {
        isFirstTrendBar = true;
        isBelow = false;
        result = Math.max(highs[i]!, maxMin);
        maxMin = lows[i]!;
        acceleration = start;
      }
    } else if (result < highs[i]!) {
      isFirstTrendBar = true;
      isBelow = true;
      result = Math.min(lows[i]!, maxMin);
      maxMin = highs[i]!;
      acceleration = start;
    }

    if (!isFirstTrendBar) {
      if (isBelow) {
        if (highs[i]! > maxMin) {
          maxMin = highs[i]!;
          acceleration = Math.min(acceleration + inc, max);
        }
      } else if (lows[i]! < maxMin) {
        maxMin = lows[i]!;
        acceleration = Math.min(acceleration + inc, max);
      }
    }

    if (isBelow) {
      result = Math.min(result, lows[i - 1]!);
      if (i > 1) result = Math.min(result, lows[i - 2]!);
    } else {
      result = Math.max(result, highs[i - 1]!);
      if (i > 1) result = Math.max(result, highs[i - 2]!);
    }
    out[i] = result;
  }
  return out;
}

/**
 * Parabolic SAR in Wilder's own order: SweeterTrade's DOTS PSAR lane, solved on
 * their exported `dotsSar` column (2026-11-02; META daily and NVDA weekly exact,
 * NVDA daily but for one bar their four-decimal export cannot decide).
 *
 * Not `psarFull` (TradingView's ta.sar), which stays as it is: the standalone
 * PSAR study is ta.sar, and so is SweeterTrade's standalone PSAR. This one
 * differs in three places:
 *
 *   - the projected SAR, sar + af * (ep - sar), is clamped to the two prior
 *     bars' lows (up) or highs (down) BEFORE the penetration test, not after;
 *   - the test is inclusive: a low AT the SAR ends an up trend, a high at it a
 *     down trend;
 *   - it starts on bar 1 from bar 0: up when close[1] > close[0], the extreme
 *     bar 1's high (low when down), the SAR bar 0's low (high).
 *
 * On a flip the SAR is max(ep, high) / min(ep, low), the extreme resets to the
 * bar's low / high and af to `start`; otherwise a new extreme moves ep and adds
 * `inc` to af, up to `max`. Bar 0 is NaN.
 */
export function wilderSarFull(
  highs: ReadonlyArray<number>,
  lows: ReadonlyArray<number>,
  closes: ReadonlyArray<number>,
  start = 0.02,
  inc = 0.02,
  max = 0.2,
): number[] {
  return wilderSarRun(highs, lows, closes, start, inc, max).sar;
}

/**
 * The side of `wilderSarFull`'s SAR on each bar: +1 while it trails an up
 * trend, -1 a down trend, 0 on bar 0. Never 0 after bar 0: on a flip bar the
 * side is the new trend's, also on the bar whose SAR is its own high or low,
 * where the DOTS lane draws no dot. What reads the PSAR as a state (the
 * scanner's `psar` and its age, the ETF boards, backtests) reads this.
 */
export function wilderSarSides(
  highs: ReadonlyArray<number>,
  lows: ReadonlyArray<number>,
  closes: ReadonlyArray<number>,
  start = 0.02,
  inc = 0.02,
  max = 0.2,
): number[] {
  return wilderSarRun(highs, lows, closes, start, inc, max).side;
}

function wilderSarRun(
  highs: ReadonlyArray<number>,
  lows: ReadonlyArray<number>,
  closes: ReadonlyArray<number>,
  start: number,
  inc: number,
  max: number,
): { sar: number[]; side: number[] } {
  const n = closes.length;
  const out: number[] = new Array(n).fill(NaN);
  const side: number[] = new Array(n).fill(0);
  if (n < 2) return { sar: out, side };

  let up = closes[1]! > closes[0]!;
  let ep = up ? highs[1]! : lows[1]!;
  let sar = up ? lows[0]! : highs[0]!;
  let af = start;

  for (let i = 1; i < n; i++) {
    let s = sar + af * (ep - sar);
    const back2 = i >= 2 ? i - 2 : i - 1;
    if (up) s = Math.min(s, lows[i - 1]!, lows[back2]!);
    else s = Math.max(s, highs[i - 1]!, highs[back2]!);

    let flipped = false;
    if (up && lows[i]! <= s) {
      up = false;
      s = Math.max(ep, highs[i]!);
      ep = lows[i]!;
      af = start;
      flipped = true;
    } else if (!up && highs[i]! >= s) {
      up = true;
      s = Math.min(ep, lows[i]!);
      ep = highs[i]!;
      af = start;
      flipped = true;
    }
    if (!flipped) {
      if (up && highs[i]! > ep) {
        ep = highs[i]!;
        af = Math.min(af + inc, max);
      } else if (!up && lows[i]! < ep) {
        ep = lows[i]!;
        af = Math.min(af + inc, max);
      }
    }
    sar = s;
    out[i] = s;
    side[i] = up ? 1 : -1;
  }
  return { sar: out, side };
}
